import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Box,
	Typography,
	Card,
	CardContent,
	Chip,
	TextField,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Paper,
	IconButton,
	Tooltip,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Menu,
	MenuItem,
	ListItemIcon,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockClockIcon from "@mui/icons-material/LockClock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UpdateIcon from "@mui/icons-material/Update";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { useNavigate } from "react-router-dom";
import { Discount_Master_store_Brands, DISOUNT_MASTER_CHANNEL_OPTIONS } from "./data";
import { Roles } from "data/authData";
import moment from "moment";

export const roleAccess = (user) => {
	let brandList = [];
	let channelList = [];
	let roles = "VIEWER";

	const brands = Discount_Master_store_Brands.map((store) => store.key);
	const channels = DISOUNT_MASTER_CHANNEL_OPTIONS.map((channel) => channel.key);
	const roleList = Roles.map((channel) => channel.key);

	if (user?.role?.["ADMIN"]) {
		roles = "ADMIN";
		brandList = brands;
		channelList = channels;
	} else {
		Object.keys(user?.role).forEach((role) => {
			if (role === "RARE_RABBIT") {
				brandList.push("RARE RABBIT");
			}
			if (role === "RARE_RABBIT_FOC") {
				brandList.push("RARE RABBIT FOC");
			}
			if (role === "RAREONES" || role === "RARE ONES") {
				brandList.push("RARE ONES");
			}
			if (brands.includes(role)) {
				brandList.push(role);
			}
			if (channels.includes(role)) {
				channelList.push(role);
			}
			if (roleList.includes(role)) {
				roles = role;
			}
		});
	}
	return { brandList, channelList, roles };
};

export const formatSchemeText = (template, buy, get) => {
	let i = 0;

	return template
		.replace(/Certain/g, () => (i++ === 0 ? buy : get))
		.replace(/\b(Value|Qty|Amount)\b/g, "")
		.replace(/\s+/g, " ")
		.trim();
};

export const getSchemeTypeValue = (scheme, fallback = "") => {
	if (Array.isArray(scheme)) return scheme[0] || fallback;
	return scheme || fallback;
};

const hasExplicitTime = (value) => {
	if (!value) return false;
	const match = String(value).match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!match) return false;
	const [, hour, minute, second = "00"] = match;
	return hour !== "00" || minute !== "00" || second !== "00";
};

const getEffectiveStartMoment = (value) => {
	if (!value) return null;
	const date = moment(value);
	if (!date.isValid()) return null;
	return hasExplicitTime(value) ? date : date.startOf("day");
};

const getEffectiveEndMoment = (value) => {
	if (!value) return null;
	const date = moment(value);
	if (!date.isValid()) return null;
	return hasExplicitTime(value) ? date : date.endOf("day");
};

const getSchemeEndMoment = (row) => {
	if (!row) return null;

	if (isForceClosed(row)) {
		return getEffectiveEndMoment(row?.close_date || row?.closed_date);
	}

	return getEffectiveEndMoment(getExtendedDate(row) || row?.end_date);
};

export const formatSchemeTypeValue = (scheme) => {
	const schemeTypes = Array.isArray(scheme) ? scheme : scheme ? [scheme] : [];
	return schemeTypes.map((type) => String(type).replaceAll("_", " ")).join(", ");
};

export const generateSchemeStr = (data, scheme) => {
	let compiledStr = [];
	data.forEach((element) => {
		const rowScheme = getSchemeTypeValue(element?.scheme_type, getSchemeTypeValue(scheme));
		const str = formatSchemeTypeValue(rowScheme);
		const getValue = rowScheme.includes("%")
			? element.percent
			: rowScheme.includes("Amount_Off")
				? element.get
				: element.get;
		if (rowScheme === "Flat_Discount_%") {
			compiledStr.push(`Flat ${element.percent}%  Discount`);
		} else if (rowScheme === "Flat_Discount_Amount" || rowScheme === "Flat_Discount_Value") {
			compiledStr.push(`Flat ${element.get} Off`);
		} else if (rowScheme.includes("product_@_Amount") && rowScheme.includes("Get_certain_amount_off")) {
			compiledStr.push(
				`Buy For ${element.buy} & Get ${element.get} & ${element.product || "product"} @ ${element.value}`
			);
		} else if (rowScheme.includes("product_@_Amount") || rowScheme.includes("product @ Amount")) {
			compiledStr.push(`Buy For ${element.buy} & Get ${element.product || "product"} @ ${element.value}`);
		} else if (rowScheme === "NOD") {
			compiledStr.push("NOD");
		} else {
			compiledStr.push(formatSchemeText(str, element.buy, getValue));
		}
	});
	return compiledStr.join(" , ");
};

export const getSchemeStatus = (data) => {
	const isPublished = data?.status === "Published";

	if (!data?.start_date || !data?.end_date) {
		return { label: "Unknown", color: "#9e9e9e", bgColor: "#f5f5f5" };
	}

	const now = moment();
	const startDate = getEffectiveStartMoment(data.start_date);
	const endDate = getSchemeEndMoment(data);

	if (!startDate || !endDate) {
		return { label: "Unknown", color: "#9e9e9e", bgColor: "#f5f5f5" };
	}

	if (isForceClosed(data)) {
		if (now.isSameOrBefore(endDate, "minute")) {
			return { label: "Active", color: "#388e3c", bgColor: "#e8f5e9" };
		}
		return { label: "Expired", color: "#d32f2f", bgColor: "#ffebee" };
	}

	if (data?.status === "Draft") {
		return { label: "Draft", color: "#616161", bgColor: "#f5f5f5" };
	}

	if (data?.status === "Submitted") {
		if (now.isAfter(endDate)) {
			return { label: "Expired", color: "#d32f2f", bgColor: "#ffebee" };
		}
		if (now.isAfter(startDate)) {
			return { label: "Unpublished", color: "#b54708", bgColor: "#ffedd5" };
		}
		return { label: "Submitted", color: "#e65100", bgColor: "#fff3e0" };
	}

	if (data?.status === "Rejected") {
		return { label: "Rejected", color: "#d32f2f", bgColor: "#fff3e0" };
	}

	if (isPublished) {
		if (now.isBefore(startDate)) {
			return { label: "Upcoming", color: "#1976d2", bgColor: "#e3f2fd" };
		}
		if (now.isAfter(endDate)) {
			return { label: "Expired", color: "#d32f2f", bgColor: "#ffebee" };
		}
		return { label: "Active", color: "#388e3c", bgColor: "#e8f5e9" };
	}

	return { label: "Unknown", color: "#9e9e9e", bgColor: "#f5f5f5" };
};

const hasStartDateExceeded = (data) => {
	if (!data?.start_date) return false;

	const now = moment();
	const startDate = getEffectiveStartMoment(data.start_date);
	if (!startDate) return false;
	return now.isAfter(startDate);
};

const getDisplayValue = (value, type) => {
	if (value === null || value === undefined || value === "") return "";

	if (typeof value === "string" || typeof value === "number") {
		return String(value).trim();
	}

	if (typeof value === "object") {
		if (type === "zone") {
			return (
				value?.zone ||
				value?.zone_name ||
				value?.zoneName ||
				value?.label ||
				value?.name ||
				""
			);
		}

		if (type === "store_grade") {
			return (
				value?.store_grade ||
				value?.storeGrade ||
				value?.grade ||
				value?.label ||
				value?.name ||
				""
			);
		}

		return value?.label || value?.name || "";
	}

	return "";
};

const getSearchableListText = (value, type) => {
	if (Array.isArray(value)) {
		return value
			.map((item) => getDisplayValue(item, type))
			.filter(Boolean)
			.join(" ")
			.toLowerCase();
	}

	return getDisplayValue(value, type).toLowerCase();
};

const getCompactListLabel = (value, type) => {
	if (Array.isArray(value)) {
		const displayValues = value.map((item) => getDisplayValue(item, type)).filter(Boolean);
		if (!displayValues.length) return "-";
		return displayValues.join(", ");
	}

	const displayValue = getDisplayValue(value, type);
	return displayValue || "-";
};

const toMySQLDateTime = (date) => {
	const pad = (n) => n.toString().padStart(2, "0");

	return (
		date.getFullYear() +
		"-" +
		pad(date.getMonth() + 1) +
		"-" +
		pad(date.getDate()) +
		" " +
		pad(date.getHours()) +
		":" +
		pad(date.getMinutes()) +
		":" +
		pad(date.getSeconds())
	);
};

const getRowRecordId = (row) => row?.id || row?._id || row?.discount_id || row?.discountId;

const hasTruthyField = (row, keys = []) =>
	keys.some((key) => {
		const value = row?.[key];
		if (typeof value === "string") {
			return ["true", "yes", "y", "1", "sent"].includes(value.trim().toLowerCase());
		}
		return Boolean(value);
	});

const hasApprovalEmailSent = (row) =>
	hasTruthyField(row, [
		"approval_email_status",
		"approval_mail_status",
		"approve_email_status",
		"approve_mail_status",
		"submitted_email_status",
		"submitted_mail_status",
		"approval_email_sent",
		"approval_mail_sent",
		"approve_email_sent",
		"approve_mail_sent",
		"submitted_email_sent",
		"submitted_mail_sent",
	]);

const hasPublishedEmailSent = (row) =>
	hasTruthyField(row, [
		"published_email_status",
		"published_mail_status",
		"publish_email_status",
		"publish_mail_status",
		"published_email_sent",
		"published_mail_sent",
		"publish_email_sent",
		"publish_mail_sent",
	]);

const hasClosedEmailSent = (row) =>
	hasTruthyField(row, [
		"closed_email_status",
		"closed_mail_status",
		"close_email_status",
		"close_mail_status",
		"closed_email_sent",
		"closed_mail_sent",
		"close_email_sent",
		"close_mail_sent",
	]);

const canShowForceCloseForClosedEmail = (row) => !hasClosedEmailSent(row);

const isForceClosed = (row) =>
	hasTruthyField(row, ["force_closed", "is_force_closed"]) ||
	["force closed", "discount closed", "closed"].includes(
		String(row?.status || "").trim().toLowerCase()
	);

const getForceCloseDate = (row) => {
	const closeDate = row?.close_date || row?.closed_date || "";
	if (closeDate && (hasExplicitTime(closeDate) || /[T ]\d{2}:\d{2}/.test(String(closeDate)))) {
		return closeDate;
	}
	return row?.closed_at || closeDate || "";
};

export const formatCloseDateDisplay = (value) => {
	if (!value) return "";
	const parsed = moment(
		value,
		[
			moment.ISO_8601,
			"YYYY-MM-DD HH:mm:ss",
			"YYYY-MM-DDTHH:mm",
			"YYYY-MM-DDTHH:mm:ss",
			"YYYY-MM-DD",
			"DD-MMM-YYYY hh:mm A",
			"DD-MM-YYYY hh:mm A",
			"DD-MMM-YYYY",
			"DD-MM-YYYY",
		],
		true
	);
	const date = parsed.isValid() ? parsed : moment(value);
	if (!date.isValid()) return String(value);
	return date.format("DD-MM-YYYY hh:mm A");
};

const getForceCloseReason = (row) =>
	row?.close_reason || row?.closed_reason || row?.force_close_reason || "";

const getRejectReason = (row) =>
	row?.rejected_reason || row?.rejection_reason || row?.reject_reason || "";

const getExtendedDate = (row) =>
	row?.extended_end_date || row?.extended_date || row?.new_end_date || row?.revised_end_date || "";

const toSortTime = (value) => {
	if (!value) return null;
	const date = moment(value);
	return date.isValid() ? date.valueOf() : null;
};

const DATE_SORT_KEYS = new Set(["date", "extended_date", "close_date", "created_at"]);

const TABLE_COLUMNS = [
	{ key: "scheme_name", label: "Scheme Name", align: "left", minWidth: 280, width: 280, pinnedLeft: true, left: 0 },
	{ key: "scheme_status", label: "Scheme Status", align: "center", minWidth: 142, width: 142, pinnedLeft: true, left: 280 },
	{ key: "record_status", label: "Record Status", align: "center", minWidth: 158, width: 158, pinnedLeft: true, left: 422, pinEdge: true },
	{ key: "date", label: "Date", align: "center", minWidth: 196, width: 196 },
	{ key: "brand", label: "Brand", align: "center", minWidth: 120, width: 120 },
	{ key: "channel", label: "Channel", align: "center", minWidth: 110, width: 110 },
	{ key: "zone", label: "Zone", align: "center", minWidth: 140, width: 140 },
	{ key: "store_grade", label: "Store Grade", align: "center", minWidth: 130, width: 130 },
	{ key: "extended_date", label: "Extended Date", align: "center", minWidth: 168, width: 168 },
	{ key: "close_date", label: "Close Date", align: "center", minWidth: 168, width: 168 },
	{ key: "close_reason", label: "Close Reason", align: "center", minWidth: 160, width: 160 },
	{ key: "reject_reason", label: "Reject Reason", align: "center", minWidth: 180, width: 180 },
	{ key: "actions", label: "Actions", align: "center", sortable: false, minWidth: 120, width: 120, pinned: true },
];

const TABLE_MIN_WIDTH = TABLE_COLUMNS.reduce((total, column) => total + (column.width || 120), 0);

const pinnedActionCellSx = {
	position: "sticky",
	right: 0,
	zIndex: 2,
	backgroundColor: "#fff",
	boxShadow: "-8px 0 8px -8px rgba(16, 24, 40, 0.28)",
};

const pinnedActionHeaderSx = {
	...pinnedActionCellSx,
	zIndex: 3,
	backgroundColor: "#f8fafc",
	"&.MuiTableCell-stickyHeader": {
		right: 0,
		zIndex: 3,
		backgroundColor: "#f8fafc",
	},
};

const pinnedLeftHeaderSx = (column) => ({
	position: "sticky",
	left: column.left,
	zIndex: 4,
	backgroundColor: "#f8fafc",
	...(column.pinEdge ? { boxShadow: "8px 0 8px -8px rgba(16, 24, 40, 0.22)" } : {}),
	"&.MuiTableCell-stickyHeader": {
		position: "sticky",
		left: column.left,
		zIndex: 4,
		backgroundColor: "#f8fafc",
	},
});

const pinnedLeftBodySx = (column) => ({
	position: "sticky",
	left: column.left,
	zIndex: 2,
	backgroundColor: "#fff",
	...(column.pinEdge ? { boxShadow: "8px 0 8px -8px rgba(16, 24, 40, 0.22)" } : {}),
	".MuiTableRow-root:hover &": {
		backgroundColor: "#f8fbff",
	},
});

const getTableColumn = (key) => TABLE_COLUMNS.find((column) => column.key === key);

const getColumnSortValue = (row, key) => {
	switch (key) {
		case "scheme_name":
			return String(row?.scheme_name || "").toLowerCase();
		case "date":
			return toSortTime(row?.start_date);
		case "brand":
			return getSearchableListText(row?.brand);
		case "channel":
			return String(row?.channel || "").toLowerCase();
		case "zone":
			return getSearchableListText(row?.zone, "zone");
		case "store_grade":
			return getSearchableListText(row?.store_grade, "store_grade");
		case "extended_date":
			return toSortTime(getExtendedDate(row));
		case "close_date":
			return toSortTime(getForceCloseDate(row));
		case "close_reason":
			return String(getForceCloseReason(row) || "").toLowerCase();
		case "reject_reason":
			return String(getRejectReason(row) || "").toLowerCase();
		case "scheme_status":
			return String(getSchemeStatus(row)?.label || "").toLowerCase();
		case "record_status":
			return isForceClosed(row)
				? "force closed"
				: String(row?.status || "").toLowerCase();
		case "created_at":
			return toSortTime(row?.created_at);
		default:
			return "";
	}
};

const compareSortValues = (aVal, bVal, direction) => {
	const aEmpty = aVal == null || aVal === "";
	const bEmpty = bVal == null || bVal === "";
	if (aEmpty && bEmpty) return 0;
	if (aEmpty) return 1;
	if (bEmpty) return -1;

	const dir = direction === "asc" ? 1 : -1;
	if (typeof aVal === "number" && typeof bVal === "number") {
		if (aVal === bVal) return 0;
		return aVal > bVal ? dir : -dir;
	}

	const cmp = String(aVal).localeCompare(String(bVal), undefined, {
		numeric: true,
		sensitivity: "base",
	});
	return cmp * dir;
};

const isDiscountCurrentlyActive = (row) => {
	if (!row?.start_date) return false;

	const startDate = getEffectiveStartMoment(row.start_date);
	const maxEndDate = getSchemeEndMoment(row);

	if (!startDate || !maxEndDate) {
		return false;
	}

	const now = moment();
	return now.isSameOrAfter(startDate) && now.isSameOrBefore(maxEndDate);
};

const isDiscountUpcomingOrActive = (row) => {
	const schemeStatus = getSchemeStatus(row)?.label;
	return schemeStatus === "Upcoming" || schemeStatus === "Active";
};

const isSchemeExpiredOrUnpublished = (row) => {
	const schemeStatus = getSchemeStatus(row)?.label;
	return schemeStatus === "Expired" || schemeStatus === "Unpublished";
};

const matchesExtendSchemeStatus = (row) => {
	const schemeStatus = getSchemeStatus(row)?.label;

	if (row?.status === "Submitted") {
		return schemeStatus === "Unpublished";
	}

	if (row?.status === "Published") {
		return schemeStatus === "Active";
	}

	return false;
};

export default function DiscountMasterList({ data = [] }) {
	const [discountList, setDiscountList] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [filteredList, setFilteredList] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
	const [actionLoading, setActionLoading] = useState({});
	const [forceCloseDialog, setForceCloseDialog] = useState({
		open: false,
		row: null,
		closeDate: moment().format("YYYY-MM-DDTHH:mm"),
		reason: "",
	});
	const [sendEmailDialog, setSendEmailDialog] = useState({
		open: false,
		row: null,
		type: "",
	});
	const [extendDialog, setExtendDialog] = useState({
		open: false,
		row: null,
		extendDate: moment().format("YYYY-MM-DDTHH:mm"),
	});
	const [actionMenu, setActionMenu] = useState({
		anchorEl: null,
		row: null,
	});
	const navigate = useNavigate();

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const roleData = roleAccess(user);
	const canForceCloseDiscount = ["ADMIN", "PUBLISHER"].includes(roleData.roles);

	const getStatusColor = (status) => {
		const statusMap = {
			Submitted: { bg: "#fff3e0", color: "#e65100", label: "Submitted" },
			Published: { bg: "#e8f5e9", color: "#2e7d32", label: "Published" },
			"Force Closed": { bg: "#fee4e2", color: "#b42318", label: "Force Closed" },
			Closed: { bg: "#fee4e2", color: "#b42318", label: "Force Closed" },
			Draft: { bg: "#f5f5f5", color: "#616161", label: "Draft" },
			Active: { bg: "#e8f5e9", color: "#2e7d32", label: "Active" },
			Expired: { bg: "#ffebee", color: "#c62828", label: "Expired" },
		};
		return statusMap[status] || { bg: "#c62828", color: "#eeeaea", label: status };
	};

	const getCurrentDateTime = () => moment().format("YYYY-MM-DDTHH:mm");

	const parsePickerDateTime = (value) => {
		if (!value) return null;
		const date = moment(value);
		return date.isValid() ? date.toDate() : null;
	};

	const formatPickerDateTime = (value) => {
		if (!value) return "";
		const date = moment(value);
		return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
	};

	const getForceCloseMaxDate = (row) => {
		const maxDateSource = getExtendedDate(row) || row?.end_date;
		if (!maxDateSource) return "";
		const endDate = getEffectiveEndMoment(maxDateSource);
		return endDate?.isValid() ? endDate.format("YYYY-MM-DDTHH:mm") : "";
	};

	const getExtendMinDate = (row) => {
		const minDateSource = getExtendedDate(row) || row?.end_date;
		if (!minDateSource) return moment().format("YYYY-MM-DDTHH:mm");
		const minDate = moment(minDateSource);
		return minDate.isValid()
			? minDate.add(1, "minute").format("YYYY-MM-DDTHH:mm")
			: moment().format("YYYY-MM-DDTHH:mm");
	};

	const handleRowClick = (row) => {
		if (row?.status !== "Draft" || roleData.roles === "VIEWER") {
			navigate(`/explore/discount_master/discount_summary`, {
				state: { data: row },
			});
			return;
		}
		navigate(`/explore/discount_master/create_discount`, {
			state: { data: row },
		});
	};

	const handleCopyDiscount = (row) => {
		navigate(`/explore/discount_master/create_discount`, {
			state: { data: row, mode: "copy" },
		});
	};

	const handleGetDiscountList = async () => {
		setLoadingOpen(true);
		const body = {
			brand: roleData?.brandList,
			channel: roleData?.channelList,
		};

		pythonVmInstance
			.post("/discount-master/get-discounts-list", body)
			.then((res) => {
				setLoadingOpen(false);
				const list = res?.data || [];
				setDiscountList(list);
				setFilteredList(list);
				setPage(0);
			})
			.catch((err) => {
				const message = err?.response?.data?.message;
				setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: message || "Failed to load discount master list",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			});
	};

	const handleSearch = (value) => {
		setSearchTerm(value);
		const search = value.toLowerCase();

		const filtered = discountList.filter((item) => {
			const brandText = Array.isArray(item.brand)
				? item.brand.join(" ").toLowerCase()
				: (item.brand || "").toLowerCase();
			const zoneText = getSearchableListText(item.zone, "zone");
			const storeGradeText = getSearchableListText(item.store_grade, "store_grade");

			return (
				item.scheme_name?.toLowerCase().includes(search) ||
				brandText.includes(search) ||
				item.channel?.toLowerCase().includes(search) ||
				zoneText.includes(search) ||
				storeGradeText.includes(search)
			);
		});

		setFilteredList(filtered);
		setPage(0);
	};

	const getSequenceOrder = (row) => {
		const schemeStatus = getSchemeStatus(row)?.label;
		const sequenceMap = {
			Draft: 1,
			Submitted: 2,
			Active: 3,
			Upcoming: 4,
			Unpublished: 5,
			Expired: 6,
			Rejected: 7,
			Published: 8,
			Unknown: 9,
		};

		if (
			schemeStatus === "Expired" ||
			schemeStatus === "Upcoming" ||
			schemeStatus === "Active" ||
			schemeStatus === "Unpublished"
		) {
			return sequenceMap[schemeStatus];
		}

		return sequenceMap[row?.status] || 99;
	};

	const handleChangePage = (_, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const updateActionLoading = (key, value) => {
		setActionLoading((prev) => ({ ...prev, [key]: value }));
	};

	const showSnackMessage = (message, severity = "success") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleSendEmail = async (row, type) => {
		const recordId = getRowRecordId(row);
		const actionLabelMap = {
			approval: "approval email",
			published: "publish email",
		};
		const actionLabel = actionLabelMap[type] || "email";
		if (!recordId) {
			showSnackMessage(`Unable to send ${actionLabel}: discount id not found`, "error");
			return;
		}

		const loadingKey = `${type}-${recordId}`;
		updateActionLoading(loadingKey, true);

		try {
			const payload = {
				type,
				triggered_by: user?.email,
				triggered_at: toMySQLDateTime(new Date()),
				status: row?.status,
			};

			const res = await pythonVmInstance.patch(
				`/discount-master/send-email/${recordId}`,
				payload
			);
			showSnackMessage(
				res?.data?.message ||
					"Mail triggered. It will take a few minutes to receive the mail."
			);
			handleGetDiscountList();
		} catch (error) {
			showSnackMessage(
				error?.response?.data?.message ||
					(type === "approval"
						? "Failed to send approval email"
						: "Failed to send publish email"),
				"error"
			);
		} finally {
			updateActionLoading(loadingKey, false);
		}
	};

	const openSendEmailDialog = (row, type) => {
		setSendEmailDialog({
			open: true,
			row,
			type,
		});
	};

	const closeSendEmailDialog = () => {
		setSendEmailDialog({
			open: false,
			row: null,
			type: "",
		});
	};

	const openForceCloseDialog = (row) => {
		const todayDate = getCurrentDateTime();
		const maxDate = getForceCloseMaxDate(row);
		const closeDate =
			maxDate && moment(todayDate).isAfter(moment(maxDate)) ? maxDate : todayDate;

		setForceCloseDialog({
			open: true,
			row,
			closeDate,
			reason: "",
		});
	};

	const closeForceCloseDialog = () => {
		setForceCloseDialog({
			open: false,
			row: null,
			closeDate: getCurrentDateTime(),
			reason: "",
		});
	};

	const openExtendDialog = (row) => {
		const nextDate = getExtendMinDate(row);
		setExtendDialog({
			open: true,
			row,
			extendDate: nextDate,
		});
	};

	const closeExtendDialog = () => {
		setExtendDialog({
			open: false,
			row: null,
			extendDate: moment().format("YYYY-MM-DDTHH:mm"),
		});
	};

	const openActionMenu = (event, row) => {
		setActionMenu({
			anchorEl: event.currentTarget,
			row,
		});
	};

	const closeActionMenu = () => {
		setActionMenu({
			anchorEl: null,
			row: null,
		});
	};

	const handleForceClose = async () => {
		const row = forceCloseDialog.row;
		const recordId = getRowRecordId(row);

		if (!recordId) {
			showSnackMessage("Unable to force close discount: discount id not found", "error");
			return;
		}
		if (!forceCloseDialog.closeDate) {
			showSnackMessage("Please select close date", "error");
			return;
		}
		if (!forceCloseDialog.reason.trim()) {
			showSnackMessage("Please enter close reason", "error");
			return;
		}

		const todayDate = moment();
		const selectedCloseDate = moment(forceCloseDialog.closeDate, "YYYY-MM-DDTHH:mm", true);
		const maxCloseDateSource = getExtendedDate(forceCloseDialog.row) || forceCloseDialog.row?.end_date;
		const endDate = maxCloseDateSource
			? moment(maxCloseDateSource)
			: null;

		if (!selectedCloseDate.isValid()) {
			showSnackMessage("Please select a valid close date", "error");
			return;
		}

		// if (selectedCloseDate.isBefore(todayDate)) {
		// 	showSnackMessage("Close date cannot be before current time", "error");
		// 	return;
		// }

		if (endDate && selectedCloseDate.isAfter(endDate)) {
			showSnackMessage("Close date cannot be after scheme max end date", "error");
			return;
		}

		const loadingKey = `force-close-${recordId}`;
		updateActionLoading(loadingKey, true);
		setLoadingOpen(true);

		try {
			const res = await pythonVmInstance.patch(`/discount-master/force-close/${recordId}`, {
				status: "Force Closed",
				closed_at: toMySQLDateTime(new Date()),
				close_date: selectedCloseDate.format("YYYY-MM-DD HH:mm:ss"),
				closed_by: user?.email,
				close_reason: forceCloseDialog.reason.trim(),
			});
			showSnackMessage(
				res?.data?.message ||
					"Discount closed successfully. It will take a few minutes to receive the mail."
			);
			closeForceCloseDialog();
			handleGetDiscountList();
		} catch (error) {
			showSnackMessage(
				error?.response?.data?.message || "Failed to force close discount",
				"error"
			);
		} finally {
			setLoadingOpen(false);
			updateActionLoading(loadingKey, false);
		}
	};

	const handleExtendDiscount = async () => {
		const row = extendDialog.row;
		const recordId = getRowRecordId(row);

		if (!recordId) {
			showSnackMessage("Unable to extend discount: discount id not found", "error");
			return;
		}

		if (!extendDialog.extendDate) {
			showSnackMessage("Please select extend date and time", "error");
			return;
		}

		const minExtendDate = moment(getExtendMinDate(row), "YYYY-MM-DDTHH:mm", true);
		const selectedExtendDate = moment(extendDialog.extendDate, "YYYY-MM-DDTHH:mm", true);

		if (!selectedExtendDate.isValid()) {
			showSnackMessage("Please select a valid extend date and time", "error");
			return;
		}

		if (selectedExtendDate.isBefore(minExtendDate)) {
			showSnackMessage("Extend date must be after current scheme end date", "error");
			return;
		}

		const loadingKey = `extend-${recordId}`;
		updateActionLoading(loadingKey, true);
		setLoadingOpen(true);

		try {
			const res = await pythonVmInstance.patch(`/discount-master/extend/${recordId}`, {
				extended_end_date: toMySQLDateTime(new Date(extendDialog.extendDate)),
				extended_by: user?.email,
				extended_at: toMySQLDateTime(new Date()),
			});
			showSnackMessage(res?.data?.message || "Discount extended successfully");
			closeExtendDialog();
			handleGetDiscountList();
		} catch (error) {
			showSnackMessage(
				error?.response?.data?.message || "Failed to extend discount",
				"error"
			);
		} finally {
			setLoadingOpen(false);
			updateActionLoading(loadingKey, false);
		}
	};

	useEffect(() => {
		handleGetDiscountList();
	}, []);

	const handleSort = (key) => {
		setPage(0);
		setSortConfig((prev) => {
			if (prev.key === key) {
				return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
			}
			return { key, direction: DATE_SORT_KEYS.has(key) ? "desc" : "asc" };
		});
	};

	const sortedFilteredList = useMemo(() => {
		const { key, direction } = sortConfig;

		return [...filteredList].sort((a, b) => {
			if (key === "created_at") {
				const aCreatedAt = a?.created_at ? new Date(a.created_at).getTime() : 0;
				const bCreatedAt = b?.created_at ? new Date(b.created_at).getTime() : 0;

				if (aCreatedAt !== bCreatedAt) {
					return direction === "asc" ? aCreatedAt - bCreatedAt : bCreatedAt - aCreatedAt;
				}

				const orderA = getSequenceOrder(a);
				const orderB = getSequenceOrder(b);

				if (orderA !== orderB) {
					return orderA - orderB;
				}

				const aStartDate = a?.start_date ? new Date(a.start_date).getTime() : 0;
				const bStartDate = b?.start_date ? new Date(b.start_date).getTime() : 0;

				return bStartDate - aStartDate;
			}

			const compared = compareSortValues(
				getColumnSortValue(a, key),
				getColumnSortValue(b, key),
				direction
			);
			if (compared !== 0) return compared;

			const aCreatedAt = a?.created_at ? new Date(a.created_at).getTime() : 0;
			const bCreatedAt = b?.created_at ? new Date(b.created_at).getTime() : 0;
			return bCreatedAt - aCreatedAt;
		});
	}, [filteredList, sortConfig]);

	const paginatedList = sortedFilteredList.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	useEffect(() => {
		const maxPage = Math.max(0, Math.ceil(sortedFilteredList.length / rowsPerPage) - 1);
		if (page > maxPage) {
			setPage(maxPage);
		}
	}, [page, rowsPerPage, sortedFilteredList.length]);

	const statusCounts = filteredList.reduce((counts, row) => {
		const schemeStatusLabel = getSchemeStatus(row)?.label || "Unknown";
		counts[schemeStatusLabel] = (counts[schemeStatusLabel] || 0) + 1;
		return counts;
	}, {});

	const quickStats = [
		{
			label: "Total Discount",
			helper: "All discounts in current view",
			value: filteredList.length,
			color: "#17202a"
		},
		{
			label: "Drafts",
			helper: "Saved but not submitted",
			value: statusCounts.Draft || 0,
			color: "#756f67",
		},
		{
			label: "Submitted",
			helper: "Waiting for publish or reject",
			value: statusCounts.Submitted || 0,
			color: "#e68414",
		},
		{
			label: "Unpublished",
			helper: "Start date passed but discount not published",
			value: statusCounts.Unpublished || 0,
			color: "#b54708",
		},
		{
			label: "Rejected",
			helper: "Rejected discounts",
			value: statusCounts.Rejected || 0,
			color: "#f50000",
		},
	];

	const canManageActions = roleData.roles !== "VIEWER";
	const canExtendDiscount = ["PUBLISHER", "ADMIN"].includes(roleData.roles);
	const actionMenuOpen = Boolean(actionMenu.anchorEl);

	return (
		<Box
			sx={{
				p: { xs: 1, md: 1.5 },
				background: "linear-gradient(180deg, #f7f9fc 0%, #fbfcfe 55%, #ffffff 100%)",
				minHeight: "100vh",
			}}
		>
			<Box
				sx={{
					mb: 1,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 1,
					flexWrap: "wrap",
				}}
			>
				<Box>
					<Typography
						sx={{
							fontSize: { xs: "20px", md: "22px" },
							fontWeight: "700",
							color: "#17202a",
							mb: 0.1,
							letterSpacing: "-0.02em",
							lineHeight: 1.1,
						}}
					>
						Discount Schemes
					</Typography>
					<Typography sx={{ fontSize: "13px", color: "#667085" }}>
						Manage and create discount schemes for your channels
					</Typography>
				</Box>

				<Button
					variant="contained"
					onClick={() => navigate(`/explore/discount_master/create_discount`)}
					startIcon={<AddIcon />}
					disabled={roleData.roles === "VIEWER"}
					sx={{
						background: "#182230",
						color: "#ffffff",
						fontWeight: "600",
						textTransform: "none",
						fontSize: "13px",
						minHeight: 36,
						padding: "6px 14px",
						borderRadius: "7px",
						boxShadow: "none",
						"&:hover": {
							background: "#243447",
							boxShadow: "none",
						},
					}}
				>
					New Discount
				</Button>
			</Box>

			<Box sx={{ mb: 1, maxWidth: 360 }}>
				<TextField
					fullWidth
					size="small"
					placeholder="Search scheme, brand, channel..."
					value={searchTerm}
					onChange={(e) => handleSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ color: "#98a2b3", fontSize: 20 }} />
							</InputAdornment>
						),
					}}
					sx={{
						"& .MuiOutlinedInput-root": {
							background: "rgba(255,255,255,0.95)",
							borderRadius: "9px",
							fontSize: "13px",
							minHeight: 40,
							"& fieldset": {
								borderColor: "#d7dee7",
							},
							"&:hover fieldset": {
								borderColor: "#bfc9d4",
							},
						},
						"& .MuiInputBase-input": {
							paddingY: "8px",
						},
					}}
				/>
			</Box>

			{filteredList.length > 0 && (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
						gap: 1,
						mb: 1,
					}}
				>
					{quickStats.map((item) => (
						<Card
							key={item.label}
							sx={{
								boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
								borderRadius: "8px",
								border: "1px solid #e6ebf1",
							}}
						>
							<CardContent sx={{ p: "10px 12px !important" }}>
								<Typography sx={{ fontSize: "13px", color: "#6b6d6e", mb: 0.1, fontWeight: "600" }}>
									{item.label}
								</Typography>
								<Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: 0.35, lineHeight: 1.2 }}>
									{item.helper}
								</Typography>
								<Typography
									sx={{
										fontSize: "20px",
										fontWeight: "700",
										color: item.color,
										lineHeight: 1.1,
									}}
								>
									{item.value}
								</Typography>
							</CardContent>
						</Card>
					))}

					<Card
						sx={{
							boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
							borderRadius: "8px",
							border: "1px solid #e6ebf1",
						}}
					>
						<CardContent sx={{ p: "10px 12px !important" }}>
							<Typography sx={{ fontSize: "13px", color: "#6b6d6e", mb: 0.1, fontWeight: "600" }}>
								Published
							</Typography>
							<Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: 0.35, lineHeight: 1.2 }}>
								Published discounts overview
							</Typography>
							<Typography
								sx={{
									fontSize: "20px",
									fontWeight: "700",
									color: "#0d6b0d",
									lineHeight: 1.1,
								}}
							>
								{filteredList.filter((s) => s.status === "Published").length}
							</Typography>

							{(filteredList.filter((s) => getSchemeStatus(s).label === "Active").length > 0 ||
								filteredList.filter((s) => getSchemeStatus(s).label === "Upcoming").length > 0 ||
								filteredList.filter((s) => getSchemeStatus(s).label === "Expired").length > 0) && (
								<Box sx={{ display: "flex", gap: 1, mt: 0.65, alignItems: "flex-start", flexWrap: "wrap" }}>
									{filteredList.filter((s) => getSchemeStatus(s).label === "Active").length > 0 && (
										<Box sx={{ minWidth: 42 }}>
											<Typography sx={{ fontSize: "11px", color: "#7b8794", lineHeight: 1.1, mb: 0.15 }}>
												Active
											</Typography>
											<Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#2e7d32", lineHeight: 1.1 }}>
												{
													filteredList.filter(
														(s) => getSchemeStatus(s).label === "Active"
													).length
												}
											</Typography>
										</Box>
									)}

									{filteredList.filter((s) => getSchemeStatus(s).label === "Upcoming").length > 0 && (
										<Box sx={{ minWidth: 54 }}>
											<Typography sx={{ fontSize: "11px", color: "#7b8794", lineHeight: 1.1, mb: 0.15 }}>
												Upcoming
											</Typography>
											<Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#1976d2", lineHeight: 1.1 }}>
												{
													filteredList.filter(
														(s) => getSchemeStatus(s).label === "Upcoming"
													).length
												}
											</Typography>
										</Box>
									)}

									{filteredList.filter((s) => getSchemeStatus(s).label === "Expired").length > 0 && (
										<Box sx={{ minWidth: 48 }}>
											<Typography sx={{ fontSize: "11px", color: "#7b8794", lineHeight: 1.1, mb: 0.15 }}>
												Expired
											</Typography>
											<Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#c62828", lineHeight: 1.1 }}>
												{
													filteredList.filter(
														(s) => getSchemeStatus(s).label === "Expired"
													).length
												}
											</Typography>
										</Box>
									)}
								</Box>
							)}
						</CardContent>
					</Card>
				</Box>
			)}

			{filteredList.length > 0 ? (
				<Paper
					sx={{
						boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
						borderRadius: "8px",
						border: "1px solid #e4e7ec",
						overflow: "hidden",
					}}
				>
					<TableContainer
						sx={{
							height: "fit-content",
							minHeight: 240,
							maxHeight: "calc(100vh - 280px)",
							overflowY: "auto",
							overflowX: "auto",
						}}
					>
						<Table
							stickyHeader
							size="small"
							sx={{
								borderCollapse: "separate",
								borderSpacing: 0,
								tableLayout: "fixed",
								minWidth: TABLE_MIN_WIDTH,
							}}
						>
						<colgroup>
							{TABLE_COLUMNS.map((column) => (
								<col key={column.key} style={{ width: column.width }} />
							))}
						</colgroup>
						<TableHead>
							<TableRow sx={{ background: "#f8fafc", borderBottom: "1px solid #e4e7ec" }}>
								{TABLE_COLUMNS.map((column) => (
									<TableCell
										key={column.key}
										sortDirection={
											column.sortable === false
												? false
												: sortConfig.key === column.key
													? sortConfig.direction
													: false
										}
										sx={{
											fontWeight: "700",
											color: "#344054",
											fontSize: "12px",
											padding: "8px 10px",
											textAlign: column.align,
											whiteSpace: "nowrap",
											background: "#f8fafc",
											...(column.minWidth ? { minWidth: column.minWidth } : {}),
											...(column.width ? { width: column.width, maxWidth: column.width } : {}),
											...(column.pinned ? pinnedActionHeaderSx : {}),
											...(column.pinnedLeft ? pinnedLeftHeaderSx(column) : {}),
										}}
									>
										{column.sortable === false ? (
											column.label
										) : (
											<TableSortLabel
												active={sortConfig.key === column.key}
												direction={
													sortConfig.key === column.key ? sortConfig.direction : "asc"
												}
												onClick={() => handleSort(column.key)}
												sx={{
													"&.MuiTableSortLabel-root": {
														color: "#344054",
														justifyContent:
															column.align === "center" ? "center" : "flex-start",
														width: "100%",
													},
													"& .MuiTableSortLabel-icon": {
														fontSize: 16,
													},
												}}
											>
												{column.label}
											</TableSortLabel>
										)}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedList.map((row, idx) => {
								const statusColor = isForceClosed(row)
									? { bg: "#fee4e2", color: "#b42318", label: "Force Closed" }
									: getStatusColor(row.status);
								const schemeStatusColor = getSchemeStatus(row);
								const isExpiredScheme = schemeStatusColor.label === "Expired";
								const isStartDateExceeded = hasStartDateExceeded(row);
								const extendedDateValue = getExtendedDate(row);
								const extendedDateMoment = getEffectiveEndMoment(extendedDateValue);
								const closeDateValue = getForceCloseDate(row);
								const closeReasonValue = getForceCloseReason(row);
								const rejectReasonValue = getRejectReason(row);
								const isRejectedRow = row?.status === "Rejected";
								const isSchemeBlockedForMailOrExtend = isSchemeExpiredOrUnpublished(row);
								const canSendApprovalMail =
									canManageActions &&
									!isRejectedRow &&
									!isSchemeBlockedForMailOrExtend &&
									row?.status === "Submitted" &&
									!hasApprovalEmailSent(row);
								const canSendPublishedMail =
									canManageActions &&
									!isRejectedRow &&
									!isSchemeBlockedForMailOrExtend &&
									row?.status === "Published" &&
									getSchemeStatus(row)?.label !== "Active" &&
									!hasPublishedEmailSent(row);
								const canForceCloseRow =
									canForceCloseDiscount &&
									!isRejectedRow &&
									row?.status === "Published" &&
									isDiscountUpcomingOrActive(row) &&
									canShowForceCloseForClosedEmail(row) &&
									!isForceClosed(row);
								const canExtendRow =
									canExtendDiscount &&
									!isRejectedRow &&
									row?.status === "Published" &&
									matchesExtendSchemeStatus(row) &&
									hasPublishedEmailSent(row) &&
									!isForceClosed(row) &&
									schemeStatusColor.label === "Active";
								const canCopyRow =
									canManageActions &&
									row?.status !== "Draft";
								const hasMenuActions =
									canSendApprovalMail ||
									canSendPublishedMail ||
									canForceCloseRow ||
									canExtendRow ||
									canCopyRow;
								return (
									<TableRow
										key={`${row?._id || row?.scheme_name || "row"}-${idx}`}
										sx={{
											"&:hover": {
												background: "#f8fbff",
											},
											borderBottom: "1px solid #eef2f6",
										}}
									>
										<TableCell
											sx={{
												fontSize: "13px",
												fontWeight: "600",
												color: "#101828",
												padding: "7px 10px",
												lineHeight: 1.35,
												minWidth: 280,
												width: 280,
												whiteSpace: "normal",
												wordBreak: "break-word",
												...pinnedLeftBodySx(getTableColumn("scheme_name")),
											}}
										>
											{row.scheme_name || "-"}
										</TableCell>
										<TableCell
											sx={{
												padding: "7px 10px",
												textAlign: "center",
												...pinnedLeftBodySx(getTableColumn("scheme_status")),
											}}
										>
											<Chip
												label={schemeStatusColor.label}
												size="small"
												sx={{
													background: schemeStatusColor.bgColor,
													color: schemeStatusColor.color,
													fontWeight: "700",
													fontSize: "12px",
													height: 26,
													maxWidth: "100%",
													"& .MuiChip-label": {
														px: 1,
													},
												}}
											/>
										</TableCell>
										<TableCell
											sx={{
												padding: "7px 10px",
												textAlign: "center",
												...pinnedLeftBodySx(getTableColumn("record_status")),
											}}
										>
											<Chip
												label={statusColor.label}
												size="small"
												sx={{
													background: statusColor.bg,
													color: statusColor.color,
													fontWeight: "700",
													fontSize: "12px",
													height: 26,
													maxWidth: "100%",
													"& .MuiChip-label": {
														px: 1,
													},
												}}
											/>
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "nowrap",
											}}
										>
											{`${moment(row.start_date).format("DD-MM-YYYY")} to ${moment(row.end_date).format("DD-MM-YYYY")}` || "-"}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
											}}
										>
											{getCompactListLabel(row.brand)}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
											}}
										>
											{row.channel || "-"}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "normal",
												wordBreak: "break-word",
												lineHeight: 1.4,
												minWidth: 120,
											}}
										>
											{getCompactListLabel(row.zone, "zone")}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "normal",
												wordBreak: "break-word",
												lineHeight: 1.4,
												minWidth: 120,
											}}
										>
											{getCompactListLabel(row.store_grade, "store_grade")}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "nowrap",
												minWidth: 140,
											}}
										>
											{extendedDateValue
												? extendedDateMoment
													? extendedDateMoment.format("DD-MM-YYYY hh:mm A")
													: extendedDateValue
												: "-"}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "nowrap",
												minWidth: 140,
											}}
										>
											{closeDateValue ? formatCloseDateDisplay(closeDateValue) : "-"}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "normal",
												wordBreak: "break-word",
												lineHeight: 1.4,
												minWidth: 150,
											}}
										>
											{closeReasonValue || "-"}
										</TableCell>
										<TableCell
											sx={{
												fontSize: "12px",
												color: "#667085",
												padding: "7px 10px",
												textAlign: "center",
												whiteSpace: "normal",
												wordBreak: "break-word",
												lineHeight: 1.4,
												minWidth: 180,
											}}
										>
											{rejectReasonValue ? (
												<Tooltip title={rejectReasonValue} placement="top">
													<Typography
														sx={{
															fontSize: "12px",
															color: "#b42318",
															lineHeight: 1.4,
															display: "-webkit-box",
															WebkitLineClamp: 2,
															WebkitBoxOrient: "vertical",
															overflow: "hidden",
														}}
													>
														{rejectReasonValue}
													</Typography>
												</Tooltip>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell
											sx={{
												padding: "7px 10px",
												textAlign: "center",
												minWidth: 120,
												width: 120,
												...pinnedActionCellSx,
												".MuiTableRow-root:hover &": {
													backgroundColor: "#f8fbff",
												},
											}}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 0.5,
													whiteSpace: "nowrap",
												}}
											>
												{row.status !== "Draft" || roleData.roles === "VIEWER" ? (
													<Tooltip title="View Summary">
														<IconButton
															size="small"
															onClick={() => handleRowClick(row)}
															sx={{
																color: "#1976d2",
																padding: "4px",
																"&:hover": { background: "#e3f2fd" },
															}}
														>
															<VisibilityIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												) : (
													<Tooltip title="Edit">
														<IconButton
															size="small"
															onClick={() => handleRowClick(row)}
															sx={{
																color: "#212121",
																padding: "4px",
																"&:hover": { background: "#f5f5f5" },
															}}
														>
														<EditIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
												{hasMenuActions && (
													<IconButton
														size="small"
														onClick={(event) => openActionMenu(event, row)}
														sx={{
															color: "#475467",
															padding: "4px",
															"&:hover": { background: "#f2f4f7" },
														}}
													>
														<MoreVertIcon fontSize="small" />
													</IconButton>
												)}
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						component="div"
						count={sortedFilteredList.length}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 25, 50, 100]}
						sx={{
							borderTop: "1px solid #e4e7ec",
							"& .MuiTablePagination-toolbar": {
								minHeight: 38,
								paddingX: 1,
								alignItems: "center",
							},
							"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
								fontSize: "12px",
								color: "#667085",
								marginBottom: 0,
								marginTop: 0,
							},
							"& .MuiInputBase-root": {
								fontSize: "12px",
								alignItems: "center",
							},
							"& .MuiTablePagination-select": {
								paddingTop: 0,
								paddingBottom: 0,
							},
							"& .MuiTablePagination-actions": {
								marginLeft: 1,
							},
						}}
					/>
				</Paper>
			) : (
				<Card
					sx={{
						boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
						borderRadius: "8px",
						border: "1px solid #e4e7ec",
						textAlign: "center",
						p: 3,
					}}
				>
					<Typography sx={{ fontSize: "16px", fontWeight: "600", color: "#667085", mb: 0.5 }}>
						No Discount Schemes Found
					</Typography>
					<Typography sx={{ fontSize: "13px", color: "#98a2b3" }}>
						{searchTerm ? "Try adjusting your search terms" : "Create a new discount scheme to get started"}
					</Typography>
				</Card>
			)}

			<Dialog
				open={forceCloseDialog.open}
				onClose={closeForceCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Force Close Discount</DialogTitle>
				<DialogContent sx={{ overflow: "visible" }}>
					<Typography sx={{ fontSize: "14px", color: "#667085", mb: 2, mt: 0.5 }}>
						Please select when this discount should be closed and enter the reason for force close.
					</Typography>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DateTimePicker
							label="Close Date & Time"
							ampm
							ampmInClock
							format="dd-MM-yyyy hh:mm a"
							value={parsePickerDateTime(forceCloseDialog.closeDate)}
							onChange={(newValue) =>
								setForceCloseDialog((prev) => ({
									...prev,
									closeDate: formatPickerDateTime(newValue),
								}))
							}
							minDateTime={parsePickerDateTime(getCurrentDateTime())}
							maxDateTime={
								parsePickerDateTime(getForceCloseMaxDate(forceCloseDialog.row)) || undefined
							}
							timeSteps={{ hours: 1, minutes: 1 }}
							slotProps={{
								textField: {
									fullWidth: true,
									sx: { mb: 2 },
								},
							}}
						/>
					</LocalizationProvider>
					<TextField
						fullWidth
						multiline
						rows={4}
						label="Reason"
						placeholder="Enter reason for force close"
						value={forceCloseDialog.reason}
						onChange={(e) =>
							setForceCloseDialog((prev) => ({ ...prev, reason: e.target.value }))
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeForceCloseDialog}>Cancel</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleForceClose}
						disabled={
							actionLoading[`force-close-${getRowRecordId(forceCloseDialog.row)}`]
						}
					>
						{actionLoading[`force-close-${getRowRecordId(forceCloseDialog.row)}`] ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							"Force Close"
						)}
					</Button>
				</DialogActions>
			</Dialog>
			<Menu
				anchorEl={actionMenu.anchorEl}
				open={actionMenuOpen}
				onClose={closeActionMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{
					sx: {
						"& .MuiMenuItem-root": {
							fontSize: "13px",
							minHeight: 36,
							paddingY: 0.75,
							paddingX: 1.5,
						},
						"& .MuiListItemIcon-root": {
							minWidth: 32,
						},
					},
				}}
			>
				{canManageActions &&
					actionMenu.row &&
					!isSchemeExpiredOrUnpublished(actionMenu.row) &&
					actionMenu.row?.status === "Submitted" &&
					!hasApprovalEmailSent(actionMenu.row) && (
						<MenuItem
							onClick={() => {
								openSendEmailDialog(actionMenu.row, "approval");
								closeActionMenu();
							}}
							disabled={actionLoading[`approval-${getRowRecordId(actionMenu.row)}`]}
						>
							<ListItemIcon>
								{actionLoading[`approval-${getRowRecordId(actionMenu.row)}`] ? (
									<CircularProgress size={14} />
								) : (
									<MailOutlineIcon fontSize="small" sx={{ color: "#e68414" }} />
								)}
							</ListItemIcon>
							Send Approval Mail
						</MenuItem>
					)}

				{canManageActions &&
					actionMenu.row &&
					!isSchemeExpiredOrUnpublished(actionMenu.row) &&
					actionMenu.row?.status === "Published" &&
					getSchemeStatus(actionMenu.row)?.label !== "Active" &&
					!hasPublishedEmailSent(actionMenu.row) && (
						<MenuItem
							onClick={() => {
								openSendEmailDialog(actionMenu.row, "published");
								closeActionMenu();
							}}
							disabled={actionLoading[`published-${getRowRecordId(actionMenu.row)}`]}
						>
							<ListItemIcon>
								{actionLoading[`published-${getRowRecordId(actionMenu.row)}`] ? (
									<CircularProgress size={14} />
								) : (
									<MailOutlineIcon fontSize="small" sx={{ color: "#1976d2" }} />
								)}
							</ListItemIcon>
							Send Publish Mail
						</MenuItem>
					)}

				{canForceCloseDiscount &&
					actionMenu.row &&
					actionMenu.row?.status === "Published" &&
					isDiscountUpcomingOrActive(actionMenu.row) &&
					canShowForceCloseForClosedEmail(actionMenu.row) &&
					!isForceClosed(actionMenu.row) && (
						<MenuItem
							onClick={() => {
								openForceCloseDialog(actionMenu.row);
								closeActionMenu();
							}}
							disabled={actionLoading[`force-close-${getRowRecordId(actionMenu.row)}`]}
						>
							<ListItemIcon>
								<LockClockIcon fontSize="small" sx={{ color: "#b42318" }} />
							</ListItemIcon>
							Force Close
						</MenuItem>
					)}

				{canExtendDiscount &&
					actionMenu.row &&
					actionMenu.row?.status === "Published" &&
					matchesExtendSchemeStatus(actionMenu.row) &&
					hasPublishedEmailSent(actionMenu.row) &&
					!isForceClosed(actionMenu.row) &&
					getSchemeStatus(actionMenu.row).label === "Active" && (
						<MenuItem
							onClick={() => {
								openExtendDialog(actionMenu.row);
								closeActionMenu();
							}}
						>
							<ListItemIcon>
								<UpdateIcon fontSize="small" sx={{ color: "#7c3aed" }} />
							</ListItemIcon>
							Extend
						</MenuItem>
					)}

				{canManageActions &&
					actionMenu.row &&
					actionMenu.row?.status !== "Draft" && (
						<MenuItem
							onClick={() => {
								handleCopyDiscount(actionMenu.row);
								closeActionMenu();
							}}
						>
							<ListItemIcon>
								<ContentCopyIcon fontSize="small" sx={{ color: "#0f766e" }} />
							</ListItemIcon>
							Copy
						</MenuItem>
					)}
			</Menu>
			<Dialog
				open={sendEmailDialog.open}
				onClose={closeSendEmailDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{sendEmailDialog.type === "approval" ? "Send Approval Mail" : "Send Publish Mail"}
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: "14px", color: "#667085", mt: 0.5 }}>
						{sendEmailDialog.type === "approval"
							? "It will send mail to approver for approval of this discount. Do you want to continue?"
							: "It will send mail to all related email ids for this published discount. Do you want to continue?"}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeSendEmailDialog}>Cancel</Button>
					<Button
						variant="contained"
						onClick={() => {
							handleSendEmail(sendEmailDialog.row, sendEmailDialog.type);
							closeSendEmailDialog();
						}}
						disabled={
							actionLoading[`${sendEmailDialog.type}-${getRowRecordId(sendEmailDialog.row)}`]
						}
					>
						{actionLoading[`${sendEmailDialog.type}-${getRowRecordId(sendEmailDialog.row)}`] ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							"Send Mail"
						)}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={extendDialog.open}
				onClose={closeExtendDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Extend Discount</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: "14px", color: "#667085", mt: 0.5, mb: 2 }}>
						Please select the new extended end date and time for this discount.
					</Typography>
					<TextField
						fullWidth
						type="datetime-local"
						label="Extend Date & Time"
						InputLabelProps={{ shrink: true }}
						value={extendDialog.extendDate}
						onChange={(e) =>
							setExtendDialog((prev) => ({ ...prev, extendDate: e.target.value }))
						}
						inputProps={{
							min: getExtendMinDate(extendDialog.row),
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeExtendDialog}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleExtendDiscount}
						disabled={actionLoading[`extend-${getRowRecordId(extendDialog.row)}`]}
					>
						{actionLoading[`extend-${getRowRecordId(extendDialog.row)}`] ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							"Extend"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
